import {createSelector} from 'reselect';
import MessageType from '../../../../enums/MessageType';
import Status from '../../../../enums/Status';
import Language from '../../../lang/language';
import {getUsedDeliveryAddressOnPaymentResolutionPage} from '../../PaymentResolutionSelectors';
import {generateBooleanSelector} from '../../SelectorGenerator';
import {
  getContactNumber, getCurrency, getSelectedResidentialAddress, getSiteCode, isCreditUsed,
} from '../../selectors';
import {deliveryCountryOrderCurrencyMatch} from '../PaymentOptionSelectors';

export const getKlarna = state => state.klarna;
export const getKlarnaCustomerDetails = state => state.klarna.klarnaCustomerDetails;
export const getPaymentMethodCategories = state => state.klarna.session.paymentMethodCategories;

export const getKlarnaErrorMessage = klarnaSDKFormat => createSelector(
  [getKlarna, isCreditUsed, deliveryCountryOrderCurrencyMatch],
  (klarna, isCreditUsed, deliveryCountryCurrencyMatch) => {
    let errorObject;
    if ((klarnaSDKFormat === 'pay_later' || klarnaSDKFormat === 'klarnaOne') && !deliveryCountryCurrencyMatch) {
      errorObject = {type: MessageType.INFO, translationKey: 'klarna.payments.country.currency.mismatch'};
    } else if (klarna.status === Status.ERROR) {
      errorObject = {type: MessageType.INFO, translationKey: 'klarna.payments.error.message'};
    } else if (isCreditUsed) {
      errorObject = {type: MessageType.INFO, translationKey: 'klarna.payments.credit.message'};
    } else if (klarna.status === Status.NOT_STARTED || klarna.status === Status.LOADING || klarna.status === Status.UPDATING) {
      return null;
    } else if (klarnaSDKFormat === 'klarnaOne') {
      const klarnaCategory = klarna[klarnaSDKFormat];
      if (klarnaCategory?.load?.show_form) {
        return null;
      }
    } else if (klarna.session.paymentMethodCategories.find(category => (category.identifier === klarnaSDKFormat))) {
      const klarnaCategory = klarna[klarnaSDKFormat];
      if (!klarnaCategory) {
        errorObject = {type: MessageType.INFO, translationKey: 'klarna.payments.ineligible.message'};
      } else if (klarnaCategory.auth && !klarnaCategory.auth.show_form) {
        errorObject = {type: MessageType.INFO, translationKey: 'klarna.payments.rejection.message'};
      } else if (klarnaCategory.load && klarnaCategory.load.show_form) {
        errorObject = null;
      } else {
        errorObject = {type: MessageType.INFO, translationKey: 'klarna.payments.error.message'};
      }
    } else {
      errorObject = {type: MessageType.INFO, translationKey: 'klarna.payments.ineligible.message'};
    }
    
    return errorObject;
  },
);

export const getKlarnaOneErrorMessage = getKlarnaErrorMessage('klarnaOne');
export const getKlarnaLaterErrorMessage = getKlarnaErrorMessage('pay_later');
export const getKlarnaNowErrorMessage = getKlarnaErrorMessage('pay_now');
export const getKlarnaSliceErrorMessage = getKlarnaErrorMessage('pay_over_time');

export const getKlarnaOneIsDisabled = generateBooleanSelector(getKlarnaOneErrorMessage);
export const getKlarnaLaterIsDisabled = generateBooleanSelector(getKlarnaLaterErrorMessage);
export const getKlarnaNowIsDisabled = generateBooleanSelector(getKlarnaNowErrorMessage);
export const getKlarnaSliceIsDisabled = generateBooleanSelector(getKlarnaSliceErrorMessage);

export const getCustomerDetailsInKlarnaSDKFormat = createSelector(
  [getKlarnaCustomerDetails],
  customerDetails => ({
    given_name: customerDetails.firstName,
    family_name: customerDetails.lastName,
    email: customerDetails.email,
  }),
);

export const getSelectedAddressInKlarnaSDKFormat = createSelector(
  [getSelectedResidentialAddress, getCustomerDetailsInKlarnaSDKFormat, getContactNumber],
  (address, customerDetails, phone) => {
    const formattedAddress = {
      ...customerDetails,
      street_address: `${address.nameNumber} ${address.streetName}`,
      street_address2: address.addressLine2,
      postal_code: address.postCode,
      city: address.townCity,
      region: address.county,
      country: address.country,
      phone,
    };
    
    const addressByCountry = {
      SE: {
        ...formattedAddress,
        street_address: `${address.streetName} ${address.nameNumber}`,
      },
    };
    
    return addressByCountry[address.country] || formattedAddress;
  },
);

export const getDeliveryAddressInKlarnaSDKFormat = createSelector(
  [getUsedDeliveryAddressOnPaymentResolutionPage, getCustomerDetailsInKlarnaSDKFormat, getContactNumber],
  (address, customerDetails, phone) => {
    const formattedAddress = {
      ...customerDetails,
      street_address: `${address.buildingNameNumber} ${address.line1}`,
      street_address2: address.line2,
      postal_code: address.postcode,
      city: address.line3,
      region: address.line4,
      country: address.countryCode,
      phone,
    };
    
    const addressByCountry = {
      SE: {
        ...formattedAddress,
        street_address: `${address.line1} ${address.buildingNameNumber}`,
      },
    };
    
    return addressByCountry[address.countryCode] || formattedAddress;
  },
);

export const getKlarnaTitle = klarnaOption => createSelector(
  [getCurrency, getSiteCode],
  (currency, siteCode) => {
    const defaultTranslationKey = `payments.klarna.${klarnaOption}`;
    const currencyTranslationKey = `${defaultTranslationKey}.${currency}`;
    const siteSpecificTranslationKey = `${defaultTranslationKey}.${siteCode}`;
    
    if (Language.doesTranslationExist(siteSpecificTranslationKey)) {
      return siteSpecificTranslationKey;
    }
    
    if (Language.doesTranslationExist(currencyTranslationKey)) return currencyTranslationKey;
    
    return defaultTranslationKey;
  },
);

export const getKlarnaOneTitle = 'checkout.payment.method.klarna.name';
export const getKlarnaLaterTitle = getKlarnaTitle('pay.later');
export const getKlarnaSliceTitle = getKlarnaTitle('pay.over.time');
